import React from 'react';
import { Link } from 'react-router-dom';
import LandingNav from '../landing/components/LandingNav';
import LandingFooter from '../landing/components/LandingFooter';
import { ROUTES } from 'config/navConfig';
import { TextEffect } from 'components/ui/text-effect';

// Feature data preserved — descriptions not rendered in §2 numbered list (Quinn style)
const features = [
  {
    title: 'Sourcing to Settlement',
    description: 'End-to-end deal lifecycle management from origination through to final settlement, with full audit trail and compliance documentation at every stage.',
  },
  {
    title: 'SPV Structuring',
    description: 'Automated special purpose vehicle formation with jurisdiction-aware templates, cap table management, and investor syndication tools built for regulated markets.',
  },
  {
    title: 'RWA Tokenisation Pathway',
    description: 'Structured pathway for real-world asset tokenisation with compliance guardrails, regulatory mapping, and institutional-grade custody integration.',
  },
  {
    title: 'Institutional Compliance',
    description: 'AIFMD, FCA, and GDPR-aligned workflows with built-in KYC/AML screening, accreditation verification, and jurisdiction-specific regulatory controls.',
  },
];

const stats = [
  { value: '$2.4B+', label: 'Assets under management' },
  { value: '140+', label: 'Deals structured' },
  { value: '12', label: 'Jurisdictions supported' },
  { value: '99.9%', label: 'Platform uptime' },
];

// Subtle geometric SVG overlay used in §3 left panel and §6 right panel
const GeometricOverlay = ({ opacity = 0.08 }) => (
  <svg
    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
        <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#9aacac" strokeWidth="0.6" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#grid)" opacity={opacity} />
  </svg>
);

export default function HomeLandingPage() {
  return (
    <div style={{ background: '#F2F0EC', fontFamily: 'inherit' }}>
      {/* LandingNav — dark variant for full-bleed hero */}
      <LandingNav dark />

      {/* ─── §1 HERO ──────────────────────────────────────────────────────────── */}
      <section
        style={{
          background: '#0a1515',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'flex-end',
          paddingBottom: '96px',
          paddingTop: '160px',
          paddingLeft: '48px',
          paddingRight: '48px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Hero background — offshore wind farm (Pexels #29074352, free commercial use) */}
        <img
          src="https://images.pexels.com/photos/29074352/pexels-photo-29074352.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt=""
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 40%' }}
        />
        {/* Overlay — keeps text readable over any photo */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,21,21,0.72)' }} />
        <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%', display: 'grid', gridTemplateColumns: '3fr 2fr', gap: '64px', alignItems: 'flex-end', position: 'relative', zIndex: 1 }}>
          {/* Left */}
          <div>
            <p
              style={{
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                color: '#D4A574',
                marginBottom: '24px',
                fontWeight: 500,
              }}
            >
              Infrastructure equity, reimagined
            </p>
            <TextEffect
              as="h1"
              per="char"
              preset="typewriter"
              delay={0.5}
              style={{
                fontSize: 'clamp(3.5rem, 6vw, 5.5rem)',
                lineHeight: 1.05,
                fontWeight: 600,
                color: '#F2F0EC',
                margin: 0,
              }}
            >
              Regulated digital rails for infrastructure equity
            </TextEffect>
          </div>

          {/* Right */}
          <div style={{ paddingBottom: '8px' }}>
            <p style={{ fontSize: '1.125rem', lineHeight: 1.7, color: 'rgba(242,240,236,0.65)', marginBottom: '32px' }}>
              SeedDrive automates the full infrastructure equity workflow — from deal origination and SPV structuring through to portfolio monitoring and regulated tokenisation — built for institutional standards.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-start' }}>
              <Link
                to={ROUTES.investorOnboarding}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: '#F2F0EC',
                  color: '#1e2b2b',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  borderRadius: 0,
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.90')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                Get Started
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </Link>
              <Link
                to="/login"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '12px 24px',
                  border: '1px solid rgba(242,240,236,0.50)',
                  color: '#F2F0EC',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textDecoration: 'none',
                  borderRadius: 0,
                  background: 'transparent',
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.80')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                Log In
              </Link>
              <span style={{ fontSize: '0.8rem', color: 'rgba(242,240,236,0.40)', marginTop: '4px' }}>
                Request a demo — <Link to="/contact-page" style={{ color: 'rgba(242,240,236,0.50)', textDecoration: 'underline' }}>get in touch</Link>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── §2 PLATFORM CAPABILITIES ─────────────────────────────────────────── */}
      <section
        style={{
          background: '#1e2b2b',
          borderTop: '1px solid rgba(237,232,223,0.10)',
          padding: '112px 48px',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'grid', gridTemplateColumns: '9fr 11fr', gap: '80px', alignItems: 'flex-start' }}>
          {/* Left */}
          <div>
            <span
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                border: '1px solid rgba(212,165,116,0.30)',
                color: '#D4A574',
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '0.10em',
                fontWeight: 600,
                marginBottom: '24px',
                borderRadius: 0,
              }}
            >
              What we build
            </span>
            <h2
              style={{
                fontSize: '2.25rem',
                lineHeight: 1.2,
                fontWeight: 600,
                color: '#F2F0EC',
                marginBottom: '24px',
              }}
            >
              Sophisticated enough for institutional deal flow.
            </h2>
            <p style={{ fontSize: '1.0625rem', lineHeight: 1.7, color: 'rgba(242,240,236,0.65)', marginBottom: '32px' }}>
              Built for infrastructure equity fund managers who operate at institutional scale. Every module reflects the rigour and precision that the asset class demands.
            </p>
            <Link
              to="/platform-overview-page"
              style={{ fontSize: '0.875rem', fontWeight: 600, color: '#F2F0EC', textDecoration: 'none', borderBottom: '1px solid rgba(212,165,116,0.60)', paddingBottom: '2px' }}
            >
              Explore Platform →
            </Link>
          </div>

          {/* Right — numbered list */}
          <div>
            {features.map((f, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '24px',
                  padding: '20px 0',
                  borderBottom: i < features.length - 1 ? '1px solid rgba(242,240,236,0.10)' : 'none',
                  borderTop: i === 0 ? '1px solid rgba(242,240,236,0.10)' : 'none',
                }}
              >
                <span style={{ fontSize: '1.0625rem', fontWeight: 600, color: '#D4A574', flexShrink: 0, minWidth: '20px' }}>
                  {i + 1}
                </span>
                <span style={{ fontSize: '1.0625rem', fontWeight: 500, color: '#F2F0EC' }}>{f.title}</span>
              </div>
            ))}
            <div style={{ marginTop: '32px' }}>
              <Link
                to="/platform-overview-page"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '10px 20px',
                  border: '1px solid rgba(242,240,236,0.30)',
                  color: '#F2F0EC',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  textDecoration: 'none',
                  borderRadius: 0,
                  background: 'transparent',
                  transition: 'opacity 0.2s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.75')}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              >
                Explore Platform →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── §3 SPLIT ABOUT ───────────────────────────────────────────────────── */}
      <section style={{ display: 'grid', gridTemplateColumns: '11fr 9fr', minHeight: '560px' }}>
        {/* Left — imagery panel — drop split-about.jpg into /public/assets/images/ */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: '#0a1515',
          }}
        >
          {/* Aerial motorway interchange (Pexels #681347, free commercial use) */}
          <img
            src="https://images.pexels.com/photos/681347/pexels-photo-681347.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt=""
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,21,21,0.38)' }} />
        </div>

        {/* Right — content card */}
        <div
          style={{
            background: '#EDE8DF',
            padding: '64px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <p
            style={{
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: '#3A5557',
              fontWeight: 600,
              marginBottom: '20px',
            }}
          >
            About SeedDrive
          </p>
          <h2
            style={{
              fontSize: '2.25rem',
              lineHeight: 1.2,
              fontWeight: 600,
              color: '#1e2b2b',
              marginBottom: '20px',
            }}
          >
            The only platform built for the full infrastructure equity lifecycle.
          </h2>
          <p style={{ fontSize: '1rem', lineHeight: 1.7, color: '#4f6363', marginBottom: '32px' }}>
            From deal origination through to asset management and regulated tokenisation, SeedDrive connects every stage of infrastructure equity into a single institutional-grade platform.
          </p>
          <Link
            to="/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 20px',
              background: '#1e2b2b',
              color: '#F2F0EC',
              fontSize: '0.875rem',
              fontWeight: 600,
              textDecoration: 'none',
              borderRadius: 0,
              alignSelf: 'flex-start',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.85')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            Sign In →
          </Link>
        </div>
      </section>

      {/* ─── §4 VALUE / STATS ─────────────────────────────────────────────────── */}
      <section style={{ background: '#F2F0EC', padding: '112px 48px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          {/* Top row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '64px', marginBottom: '64px' }}>
            <div style={{ flex: '0 0 auto', maxWidth: '560px' }}>
              <h2
                style={{
                  fontSize: 'clamp(2.5rem, 4vw, 4rem)',
                  lineHeight: 1.1,
                  fontWeight: 600,
                  color: '#1e2b2b',
                  margin: 0,
                }}
              >
                Institutional Precision.
              </h2>
            </div>
            <div style={{ flex: '1', paddingTop: '8px', maxWidth: '360px' }}>
              <p style={{ fontSize: '1rem', lineHeight: 1.7, color: '#4f6363', margin: 0 }}>
                Join institutional investors and sponsors already using SeedDrive to manage infrastructure equity with the rigour and transparency that the asset class demands.
              </p>
            </div>
          </div>

          {/* Stats grid — gap-px "box of boxes" effect */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1px', background: '#e0e0e0' }}>
            {stats.map((s) => (
              <div key={s.label} style={{ background: '#FFFFFF', padding: '32px' }}>
                <p
                  style={{
                    fontSize: 'clamp(2.5rem, 4vw, 3.5rem)',
                    fontWeight: 600,
                    color: '#1e2b2b',
                    lineHeight: 1.1,
                    margin: '0 0 8px 0',
                  }}
                >
                  {s.value}
                </p>
                <p style={{ fontSize: '0.875rem', color: '#4f6363', margin: 0 }}>{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── §5 TRUSTED BY ────────────────────────────────────────────────────── */}
      <section
        style={{
          background: '#F7F5F0',
          borderTop: '1px solid #d4cec7',
          padding: '56px 48px',
        }}
      >
        <div style={{ maxWidth: '1400px', margin: '0 auto', textAlign: 'center' }}>
          <p
            style={{
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: '#9aacac',
              marginBottom: '24px',
            }}
          >
            Trusted by institutional investors and sponsors
          </p>
          <p style={{ fontSize: '0.875rem', color: '#9aacac', letterSpacing: '0.04em' }}>
            Meridian Capital &nbsp;·&nbsp; Harbourview Capital &nbsp;·&nbsp; Vantage Partners &nbsp;·&nbsp; Cornerstone AM &nbsp;·&nbsp; Bridgepoint
          </p>
        </div>
      </section>

      {/* ─── §6 SPLIT CTA ─────────────────────────────────────────────────────── */}
      <section style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: '480px' }}>
        {/* Left — cream */}
        <div
          style={{
            background: '#F2F0EC',
            padding: '64px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <p
            style={{
              fontSize: '0.7rem',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: '#D4A574',
              fontWeight: 600,
              marginBottom: '16px',
            }}
          >
            Get started
          </p>
          <h2
            style={{
              fontSize: '2.5rem',
              lineHeight: 1.15,
              fontWeight: 600,
              color: '#1e2b2b',
              marginBottom: '32px',
            }}
          >
            Ready to streamline your infrastructure equity workflow?
          </h2>
          <Link
            to={ROUTES.investorOnboarding}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: '#1e2b2b',
              color: '#F2F0EC',
              fontSize: '0.875rem',
              fontWeight: 600,
              textDecoration: 'none',
              borderRadius: 0,
              alignSelf: 'flex-start',
              marginBottom: '16px',
              transition: 'opacity 0.2s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.85')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            Get Started →
          </Link>
          <Link
            to="/contact-page"
            style={{ fontSize: '0.8rem', color: '#9aacac', textDecoration: 'none', borderBottom: '1px solid #d4cec7', paddingBottom: '2px', alignSelf: 'flex-start' }}
          >
            Request a demo
          </Link>
        </div>

        {/* Right — dark visual weight — drop cta-split.jpg into /public/assets/images/ */}
        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            background: '#0a1515',
          }}
        >
          {/* Dark mountain valley at sunset (Pexels #4326307, free commercial use) */}
          <img
            src="https://images.pexels.com/photos/4326307/pexels-photo-4326307.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt=""
            aria-hidden="true"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
          />
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,21,21,0.68)' }} />
        </div>
      </section>

      {/* ─── §7 FOOTER CTA ────────────────────────────────────────────────────── */}
      <section
        style={{
          background: '#0a1515',
          padding: '96px 48px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Aerial forest and river landscape (Pexels #9268741, free commercial use) */}
        <img
          src="https://images.pexels.com/photos/9268741/pexels-photo-9268741.jpeg?auto=compress&cs=tinysrgb&w=1920"
          alt=""
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top' }}
        />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(10,21,21,0.80)' }} />
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'grid', gridTemplateColumns: '3fr 2fr', gap: '64px', alignItems: 'center', position: 'relative', zIndex: 1 }}>
          {/* Left — large display */}
          <h2
            style={{
              fontSize: 'clamp(3.5rem, 5vw, 5rem)',
              lineHeight: 1.05,
              fontWeight: 600,
              color: '#D4A574',
              margin: 0,
            }}
          >
            Infrastructure equity, structured for institutions.
          </h2>

          {/* Right — nav links */}
          <div>
            {[
              { label: 'Platform', path: '/platform-overview-page' },
              { label: 'About', path: '/about-page' },
              { label: 'Contact', path: '/contact-page' },
            ].map((item, i, arr) => (
              <div
                key={item.label}
                style={{ borderBottom: i < arr.length - 1 ? '1px solid rgba(237,232,223,0.12)' : 'none', padding: '14px 0' }}
              >
                <Link
                  to={item.path}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: 'rgba(242,240,236,0.60)',
                    fontSize: '0.9rem',
                    textDecoration: 'none',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#F2F0EC')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(242,240,236,0.60)')}
                >
                  {item.label}
                  <span style={{ fontSize: '0.75rem' }}>↓</span>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <div
        style={{
          background: '#1e2b2b',
          borderTop: '1px solid rgba(237,232,223,0.08)',
          padding: '32px 48px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#F2F0EC' }}>SeedDrive</span>
        <span style={{ fontSize: '0.75rem', color: 'rgba(242,240,236,0.30)' }}>
          © {new Date().getFullYear()} SeedDrive. All rights reserved.
        </span>
      </div>
    </div>
  );
}
